import React from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Core/Button';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import Select from 'components/Core/Select';
import TextField from 'components/Core/TextField';
import { getFormValid } from 'utils/formUtils';
import EmbedForm from '../EmbeddableForm';
import EmbedLink from '../EmbedLink';
import { AvgGasRate, ClimateRegion, AFUEValues } from './content';

const SeerEnergyCalc = ({
  isEmbed,
  openCopyEmbedLinkDialog,
  onCalc,
  pathUrl,
  saveValues,
  ...otherProps
}) => {
  const { values, setFieldValue, handleSubmit, errors, touched, submitCount } = otherProps;

  const formValid = getFormValid({ errors, submitCount });

  const onLocationChange = (id, value) => {
    const electValue = AvgGasRate[ClimateRegion.find((f) => f.value === value).label];
    const newVals = { ...values };
    newVals['energyRate'] = electValue;
    newVals[id] = value;
    setFieldValue('energyRate', electValue);
    setFieldValue(id, value);
    saveValues(newVals);
  };

  const handleChange = (id, value) => {
    setFieldValue(id, value);
    const newVals = { ...values };
    newVals[id] = value;
    saveValues(newVals);
  };

  return (
    <div className="hvac-form-right-gate-container">
      <div className="hvac-form-total-calculation">
        <div className="hvac-form--form" onSubmit={handleSubmit} data-position="hvac seer form">
          <FormLabelWithField
            label="Location of install"
            formControlProps={{ className: 'hvac-form--form-item' }}
          >
            <Select
              className="seers-calc-drop-down"
              id="location"
              formValid={formValid}
              placeholder="Select climate region"
              options={ClimateRegion}
              handleChange={onLocationChange}
              values={values}
            />
          </FormLabelWithField>

          {values['location'] && (
            <>
              <FormLabelWithField
                label="Average energy cost"
                formControlProps={{ className: 'hvac-form--form-item' }}
              >
                <TextField
                  className="seers-calc-drop-down"
                  id="energyRate"
                  type="number"
                  values={values}
                  formValid={formValid}
                  placeholder="Average energy cost"
                  handleChange={handleChange}
                  showOnlyFormError
                />
              </FormLabelWithField>
              <p className="hvac-form--electrate-description">
                2020 data. Dollars per Thousand Cubic Feet
              </p>
            </>
          )}

          <FormLabelWithField
            label="Average BTU Per Heating Season"
            formControlProps={{ className: 'hvac-form--form-item' }}
          >
            <TextField
              className="seers-calc-drop-down"
              id="avgBTUSeason"
              type="number"
              values={values}
              formValid={formValid}
              placeholder="Average BTU Per Heating Season"
              handleChange={handleChange}
              showOnlyFormError
            />
          </FormLabelWithField>

          <FormLabelWithField
            label="Current AFUE"
            helperText="AFUE is a measure of how efficient the appliance is in converting the energy from fuel to heat over the course of a typical year. Specifically, AFUE is the ratio of the furnace's or boiler's annual heat output compared to its total annual fossil fuel energy consumed. An AFUE of 90% means that 90% of the energy in the fuel becomes heat for the home and the other 10% escapes up the chimney and elsewhere."
            formControlProps={{ className: 'hvac-form--form-item' }}
          >
            <Select
              className="seers-calc-drop-down"
              id="currentEfficiency"
              values={values}
              formValid={formValid}
              placeholder="Select Current AFUE"
              options={AFUEValues}
              handleChange={handleChange}
            />
          </FormLabelWithField>

          <FormLabelWithField
            label="New AFUE"
            helperText="AFUE is a measure of how efficient the appliance is in converting the energy from fuel to heat over the course of a typical year. Specifically, AFUE is the ratio of the furnace's or boiler's annual heat output compared to its total annual fossil fuel energy consumed. An AFUE of 90% means that 90% of the energy in the fuel becomes heat for the home and the other 10% escapes up the chimney and elsewhere."
            formControlProps={{ className: 'hvac-form--form-item' }}
          >
            <Select
              className="seers-calc-drop-down"
              id="newEfficiency"
              values={values}
              formValid={formValid}
              placeholder="Select New AFUE"
              options={AFUEValues}
              handleChange={handleChange}
            />
          </FormLabelWithField>
        </div>
        <Button
          dataPosition="Form - Calculate CTA"
          className="hvac-form-calc-button"
          onClick={() => handleSubmit()}
          button
        >
          Calculate
        </Button>
        <div className="form-embed-link">
          <EmbedLink
            show={!isEmbed}
            openCopyEmbedLinkDialog={openCopyEmbedLinkDialog}
            label="Add calculator to your website"
            pathUrl={pathUrl}
          />
        </div>
      </div>
    </div>
  );
};

const vSchema = Yup.object().shape({
  location: Yup.number().required('Location is required'),
  energyRate: Yup.string().required('Average energy cost'),
  currentEfficiency: Yup.number().required('Current AFUE is required'),
  newEfficiency: Yup.number().required('New AFUE is required'),
  avgBTUSeason: Yup.number().required('Average BTU Per Heating Season'),
});

export default EmbedForm(
  withFormik({
    validationSchema: vSchema,
    mapPropsToValues: () => ({ avgBTUSeason: 100000000 }),
    handleSubmit: (values, { props }) => {
      props.onCalc(values);
    },
  })(SeerEnergyCalc),
);
